import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { styled } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { MaterialDesignContent, SnackbarProvider } from "notistack";
import { BrowserRouter } from "react-router-dom";
import {
  RavenAppearanceProvider,
  type RavenAppearanceThemeContext,
} from "@ravenappdev/raven-ui";
import { buildEssTheme } from "./theme";
import { AuthProvider } from "./context/AuthContext";
import { TopBarActionsProvider } from "./context/TopBarActionsContext";
import ColorSchemeSync from "./theme/ColorSchemeSync";
import ErrorBoundary from "./components/ErrorBoundary";
import AppRoutes from "./Routes";

// Notistack's default colors ignore the MUI theme palette. Pin each
// variant to the theme value so snackbar fills match the rest of the
// app (e.g. success uses theme.palette.success.main, not notistack's
// #43a047). Text follows each slot's contrastText — dark mode lifts the
// fills to light tints that white text cannot sit on.
const StyledMaterialDesignContent = styled(MaterialDesignContent)(
  ({ theme: t }) => ({
    "&.notistack-MuiContent-success": {
      backgroundColor: t.palette.success.main,
      color: t.palette.success.contrastText,
    },
    "&.notistack-MuiContent-error": {
      backgroundColor: t.palette.error.main,
      color: t.palette.error.contrastText,
    },
    "&.notistack-MuiContent-warning": {
      backgroundColor: t.palette.warning.main,
      color: t.palette.warning.contrastText,
    },
    "&.notistack-MuiContent-info": {
      backgroundColor: t.palette.info.main,
      color: t.palette.info.contrastText,
    },
  }),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, retry: 1 },
  },
});

// Module-level so the provider's `useMemo` sees a stable identity and only
// rebuilds the theme when the mode or font preference actually changes.
const resolveEssTheme = ({ mode, font }: RavenAppearanceThemeContext) =>
  buildEssTheme(mode, font);

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/* The package provider owns the mode + font preference, mounts
          RavenThemeCssVars (mirroring the active palette into the --raven-*
          custom properties the design system's plain stylesheets read) and
          CssBaseline, and re-runs `resolveTheme` on every change.

          `defaultMode: "light"` rather than the provider's own "system":
          dark mode is new here, and a user whose OS is dark should not have
          the app change under them on the deploy that ships it. They opt in
          from the account menu's Appearance row, and the choice persists. */}
      <RavenAppearanceProvider
        resolveTheme={resolveEssTheme}
        defaultMode="light"
        modeStorageKey="ess.appearance.mode"
        fontStorageKey="ess.appearance.font"
      >
        <ColorSchemeSync />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <SnackbarProvider
            anchorOrigin={{ horizontal: "center", vertical: "top" }}
            autoHideDuration={3500}
            Components={{
              success: StyledMaterialDesignContent,
              error: StyledMaterialDesignContent,
              warning: StyledMaterialDesignContent,
              info: StyledMaterialDesignContent,
            }}
          >
            <AuthProvider>
              <BrowserRouter>
                <TopBarActionsProvider>
                  <ErrorBoundary>
                    <AppRoutes />
                  </ErrorBoundary>
                </TopBarActionsProvider>
              </BrowserRouter>
            </AuthProvider>
          </SnackbarProvider>
        </LocalizationProvider>
      </RavenAppearanceProvider>
    </QueryClientProvider>
  );
}
